import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

import RoyaleSettings from '@/Screens/FunChallenges/components/RoyaleSettings';
import {DEFAULT_ROYALE_SETTINGS} from '@/Screens/FunChallenges/royaleConstants';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';

const TEAL = '#3DAB9E';

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Fires with the chosen settings when the coach taps Create Puzzle Royale. */
  onCreate: (settings: RoyaleSettingsValue) => void;
  /**
   * The classroom's batch/class type (from webinarData). When it is a
   * "Demo Call" class, the modal shows the curated Demo assessment picker
   * (Demo B / F1 / F2) INSTEAD of the difficulty chips, and the coach must pick
   * an assessment before Create unlocks. Any other class type behaves as before.
   */
  batchType?: string;
}

/** True when the class is a Demo Call (tolerant of "Demo Call" / "demo_call" / "democall"). */
function isDemoCallBatch(batchType?: string): boolean {
  const norm = (batchType || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return norm === 'demo_call' || norm === 'democall' || norm === 'demo';
}

/**
 * Coach-facing modal in the classroom: shows the full Puzzle Royale options
 * (topics / time / difficulty / punish) AND the Create button in the same
 * sheet. On Create we hand the settings back; the classroom screen stashes them
 * and opens the royale lobby in a new tab to actually create the lobby + notify
 * students.
 *
 * In a Demo Call class the Difficulty section is REPLACED by the curated Demo
 * assessment picker, and the settings carry `assessment` (demo_b/f1/f2) which
 * the server routes to the fixed classroom puzzle set.
 */
export default function RoyaleCreateModal({
  visible,
  onClose,
  onCreate,
  batchType,
}: Props) {
  const isDemoCall = isDemoCallBatch(batchType);
  const [settings, setSettings] = useState<RoyaleSettingsValue>(
    DEFAULT_ROYALE_SETTINGS,
  );

  // In a Demo Call class the coach must pick an assessment; otherwise Create is
  // always enabled (topics are optional for a normal royale).
  const canCreate = !isDemoCall || !!settings.assessment;

  return (
    <SlideModal
      visible={visible}
      onClose={onClose}
      modalHeaderTitle="Create Puzzle Royale"
      type="center"
      contentPadding={20}>
      <View style={styles.body}>
        {/* Classroom coaches get the extra F1–F4 rating-band difficulties
            (allowRatingBands). In a Demo Call class we instead show the curated
            Demo assessment picker (allowDemoAssessments), which REPLACES the
            difficulty chips. Standalone royale gets neither. */}
        <RoyaleSettings
          value={settings}
          onChange={setSettings}
          allowRatingBands={!isDemoCall}
          allowDemoAssessments={isDemoCall}
        />
        <Pressable
          onPress={() => canCreate && onCreate(settings)}
          disabled={!canCreate}
          style={[styles.createBtn, !canCreate && styles.createBtnDisabled]}>
          <CCText
            fontSize={FontSizes[14]}
            fontWeight={600}
            color={CCColors.White}>
            {isDemoCall && !settings.assessment
              ? 'Pick an assessment'
              : 'Create Puzzle Royale'}
          </CCText>
        </Pressable>
      </View>
    </SlideModal>
  );
}

const styles = StyleSheet.create({
  body: {
    gap: 16,
  },
  createBtn: {
    backgroundColor: TEAL,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createBtnDisabled: {
    opacity: 0.5,
  },
});
